import { useState } from 'react';
import { Rocket, Building2, User, CheckCircle, ArrowRight, ArrowLeft } from 'lucide-react';
import { AppLayout } from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PageHeader } from '../components/ui/page-header';
import { quickCreateTenant, getApiError } from '../services/api';
import { cn } from '../lib/utils';

const steps = [
  { id: 1, label: 'Организация', icon: Building2 },
  { id: 2, label: 'Администратор', icon: User },
  { id: 3, label: 'Подтверждение', icon: CheckCircle },
];

interface FormState {
  tenantName: string;
  inn: string;
  address: string;
  administratorEmail: string;
  administratorPassword: string;
}

const initialForm: FormState = {
  tenantName: '',
  inn: '',
  address: '',
  administratorEmail: '',
  administratorPassword: '',
};

export const OnboardingPage = () => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<FormState>(initialForm);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState<{ tenantId: number; name: string }>();

  const update = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((c) => ({ ...c, [field]: e.target.value }));
    setError('');
  };

  const canNext = () => {
    if (step === 1) return form.tenantName.trim().length > 0 && form.inn.trim().length >= 10 && form.address.trim().length > 0;
    if (step === 2) return form.administratorEmail.includes('@') && form.administratorPassword.length >= 10;
    return true;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const response = await quickCreateTenant(form);
      setCreated(response.data.data);
      setSuccess(`Организация "${form.tenantName}" создана!`);
      setStep(3);
    } catch (e) {
      setError(getApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppLayout title="Онбординг">
      <PageHeader title="Быстрое добавление клиента" description="Создайте новую организацию за 2 минуты. Все настройки будут настроены автоматически." />

      <div className="mx-auto max-w-2xl">
        {/* Progress */}
        <div className="mb-8 flex items-center justify-between">
          {steps.map((s, i) => (
            <div key={s.id} className="flex items-center">
              <div className="flex items-center gap-3">
                <div className={cn(
                  'flex h-10 w-10 items-center justify-center rounded-full border-2 transition-colors',
                  step >= s.id
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border bg-muted text-muted-foreground'
                )}>
                  <s.icon className="h-5 w-5" />
                </div>
                <span className={cn('text-sm font-semibold hidden sm:block', step >= s.id ? 'text-foreground' : 'text-muted-foreground')}>
                  {s.label}
                </span>
              </div>
              {i < steps.length - 1 && <div className={cn('mx-3 h-0.5 w-12 sm:w-20', step > s.id ? 'bg-primary' : 'bg-border')} />}
            </div>
          ))}
        </div>

        {/* Error / Success */}
        {error && <div className="mb-4 rounded-xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}
        {success && step === 3 && <div className="mb-4 rounded-xl border border-success/20 bg-success/10 px-4 py-3 text-sm text-success">{success}</div>}

        {/* Step 1: Organization */}
        {step === 1 && (
          <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
            <h2 className="mb-1 text-lg font-bold">Данные организации</h2>
            <p className="mb-6 text-sm text-muted-foreground">Введите название и ИНН новой медицинской организации.</p>
            <div className="space-y-4">
              <Input required label="Название организации *" placeholder="Например: Лукойл, СтройМаш" value={form.tenantName} onChange={update('tenantName')} />
              <Input required label="ИНН *" placeholder="10-12 цифр" value={form.inn} onChange={update('inn')} inputMode="numeric" maxLength={12} />
              <Input required label="Адрес *" placeholder="Юридический адрес организации" value={form.address} onChange={update('address')} />
            </div>
          </div>
        )}

        {/* Step 2: Administrator */}
        {step === 2 && (
          <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
            <h2 className="mb-1 text-lg font-bold">Администратор организации</h2>
            <p className="mb-6 text-sm text-muted-foreground">Email и пароль для входа в личный кабинет администратора.</p>
            <div className="space-y-4">
              <Input required type="email" label="Email администратора *" placeholder="admin@company.ru" value={form.administratorEmail} onChange={update('administratorEmail')} />
              <Input required type="password" label="Временный пароль *" placeholder="Минимум 10 символов" minLength={10} value={form.administratorPassword} onChange={update('administratorPassword')} />
            </div>
          </div>
        )}

        {/* Step 3: Confirmation */}
        {step === 3 && created && (
          <div className="rounded-2xl border border-success/30 bg-success/5 p-6 shadow-sm">
            <div className="mb-6 flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-success/15 text-success">
                <CheckCircle className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-foreground">Клиент создан!</h2>
                <p className="text-sm text-muted-foreground">Организация готова к работе</p>
              </div>
            </div>
            <div className="rounded-xl border border-border bg-background p-4">
              <div className="grid gap-3 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Организация:</span><span className="font-semibold">{form.tenantName}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">ИНН:</span><span className="font-mono">{form.inn}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Email:</span><span className="font-mono">{form.administratorEmail}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Tenant ID:</span><span className="font-mono">#{created.tenantId}</span></div>
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <Button variant="outline" onClick={() => { setForm(initialForm); setCreated(undefined); setSuccess(''); setStep(1); }}>
                <Rocket className="h-4 w-4" /> Добавить ещё
              </Button>
            </div>
          </div>
        )}

        {/* Navigation */}
        {step < 3 && (
          <div className="mt-6 flex justify-between">
            <Button variant="ghost" disabled={step === 1} onClick={() => setStep((s) => s - 1)}>
              <ArrowLeft className="h-4 w-4" /> Назад
            </Button>
            {step < 2 ? (
              <Button disabled={!canNext()} onClick={() => setStep((s) => s + 1)}>
                Далее <ArrowRight className="h-4 w-4" />
              </Button>
            ) : (
              <Button disabled={!canNext() || submitting} onClick={handleSubmit}>
                {submitting ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" /> : <CheckCircle className="h-4 w-4" />}
                {submitting ? 'Создание...' : 'Создать организацию'}
              </Button>
            )}
          </div>
        )}
      </div>
    </AppLayout>
  );
};
